<!-- src/views/RecogidaView.vue -->
<template>
  <div class="min-h-screen bg-gray-50" id="planilla-content">
    
    <!-- Header oficial -->
    <header class="bg-gradient-to-r from-blue-800 to-blue-600 text-white shadow-lg">
      <div class="max-w-4xl mx-auto px-4 py-5">
        
        <!-- Logo y título principal -->
        <div class="flex items-center gap-3 mb-4">
          <!-- Logo TS con borde animado -->
            <div class="relative flex-shrink-0">
              <div class="absolute inset-0 bg-gradient-to-br from-cyan-400 to-lime-400 rounded-full blur opacity-50 animate-pulse"></div>
              <div class="relative w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gray-900 flex items-center justify-center border-2 border-cyan-400/50">
                <span class="text-xl sm:text-2xl font-bold bg-gradient-to-br from-cyan-400 to-lime-400 bg-clip-text text-transparent">TS</span>
              </div>
            </div>
          <div>
            <h1 class="text-xl md:text-2xl font-bold">Planilla de Pasajeros</h1>
            <p class="text-blue-100 text-sm">Registro oficial y control de pasajeros para servicio de transporte</p>
          </div>
        </div>

        <!-- Info del servicio (cards compactas para móvil) -->
        <div class="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
          <div class="bg-white/10 backdrop-blur-sm rounded-lg p-3">
            <p class="text-xs text-blue-200">Destino / Tour</p>
            <p class="font-semibold text-sm md:text-base truncate">{{ destino || '—' }}</p>
          </div>
          <div class="bg-white/10 backdrop-blur-sm rounded-lg p-3">
            <p class="text-xs text-blue-200">Fecha Servicio</p>
            <p class="font-semibold text-sm md:text-base">{{ formatDate(fechaServicio) }}</p>
          </div>
          <div class="bg-white/10 backdrop-blur-sm rounded-lg p-3">
            <p class="text-xs text-blue-200">Grupo</p>
            <p class="font-semibold text-sm md:text-base">G-{{ grupo }}</p>
          </div>
          <div class="bg-white/10 backdrop-blur-sm rounded-lg p-3">
            <p class="text-xs text-blue-200">Total Pax</p>
            <p class="font-semibold text-sm md:text-base">{{ cantidadPax }}</p>
          </div>
        </div>

        <!-- Conductor y Guía -->
        <div class="grid grid-cols-2 gap-2 md:gap-3 mt-3">
          <div class="bg-green-600/20 rounded-lg p-3 border border-green-500/30">
            <p class="text-xs text-green-200 flex items-center gap-1">
              <span>👨‍✈️</span> Conductor
            </p>
            <p class="font-semibold text-sm md:text-base truncate">{{ conductor || 'Sin asignar' }}</p>
          </div>
          <div class="bg-purple-600/20 rounded-lg p-3 border border-purple-500/30">
            <p class="text-xs text-purple-200 flex items-center gap-1">
              <span>🎫</span> Guía
            </p>
            <p class="font-semibold text-sm md:text-base truncate">{{ guia || 'Sin asignar' }}</p>
          </div>
        </div>

      </div>
    </header>

    <!-- Contenido principal -->
    <main class="max-w-4xl mx-auto px-4 py-6">
      
      <!-- Loading -->
      <div v-if="loading" class="flex flex-col items-center justify-center py-16">
        <div class="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent mb-3"></div>
        <p class="text-gray-600 text-sm">Cargando planilla...</p>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-xl p-5 text-center">
        <span class="text-3xl mb-2 block">⚠️</span>
        <h3 class="text-base font-semibold text-red-800 mb-1">Error al cargar</h3>
        <p class="text-red-600 text-sm mb-3">{{ error }}</p>
        <button 
          @click="fetchRecogida" 
          class="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg transition-colors"
        >
          Reintentar
        </button>
      </div>

      <!-- Datos cargados -->
      <div v-else class="space-y-5">
        
        <!-- Tabla de pasajeros (optimizada para móvil) -->
        <div class="bg-white rounded-xl shadow border border-gray-200 overflow-hidden">
          
          <div class="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
            <h2 class="text-base font-semibold text-gray-800">
              📋 Pasajeros 
            </h2>
            <!-- Botón PDF -->
            <button 
              @click="exportToPDF"
              :disabled="loading || listado.length === 0"
              class="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white text-xs font-medium rounded-lg transition-colors"
            >
              <span>📄</span>
              <span class="hidden sm:inline">Exportar PDF</span>
              <span class="sm:hidden">PDF</span>
            </button>
          </div>
          
          <!-- Mobile: Cards (prioridad) -->
          <div class="md:hidden divide-y divide-gray-100">
            <div 
              v-for="(item, index) in listado" 
              :key="index"
              class="p-4 space-y-3 bg-white border-2 border-gray-300 rounded-xl shadow-sm mb-3"
            >
              <!-- Header de la card -->
              <div class="flex items-start justify-between">
                <div>
                  <p class="text-xs text-gray-500">Pasajero #{{ index + 1 }}</p>
                  <h3 class="font-bold text-gray-900 text-base">{{ item.nombre }}</h3>
                </div>
                <span class="px-2 py-0.5 rounded-full text-xs font-bold bg-blue-100 text-blue-800">
                  {{ item.hora }}
                </span>
              </div>
              
              <!-- Datos en grid 2x2 -->
              <div class="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <div>
                  <span class="text-gray-500 text-xs block">Pax</span>
                  <p class="font-medium text-gray-800">{{ item.pasajeros }}</p>
                </div>
                <div>
                  <span class="text-gray-500 text-xs block">Encuentro</span>
                  <p class="font-medium text-gray-800">{{ item.encuentro }}</p>
                </div>
                <div>
                  <span class="text-gray-500 text-xs block">Habitación</span>
                  <p class="font-medium text-gray-800">{{ item.habitacion || '—' }}</p>
                </div>
                <div>
                  <span class="text-gray-500 text-xs block">Contacto</span>
                  <div class="flex flex-wrap gap-2">
                    <!-- Botón WhatsApp (prioritario) -->
                    <a 
                      v-if="item.telefono" 
                      :href="getWhatsAppLink(item.telefono, item.nombre)"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="inline-flex items-center gap-1 px-2 py-1 bg-green-100 hover:bg-green-200 text-green-800 text-xs font-medium rounded-lg transition-colors"
                    >
                      <span>💬</span>
                      <span>WhatsApp</span>
                    </a>
                    <!-- Botón llamada tradicional (secundario) -->
                    <a 
                      v-if="item.telefono"
                      :href="'tel:' + item.telefono"
                      class="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 hover:bg-blue-200 text-blue-800 text-xs font-medium rounded-lg transition-colors"
                    >
                      <span>📞</span>
                      <span>{{ item.telefono }}</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Desktop: Tabla tradicional -->
          <div class="hidden md:block overflow-x-auto">
            <table class="w-full text-sm">
              <thead class="bg-gray-100 text-gray-700">
                <tr>
                  <th class="px-4 py-3 text-left font-semibold">Hora</th>
                  <th class="px-4 py-3 text-left font-semibold">Pasajero</th>
                  <th class="px-4 py-3 text-left font-semibold">Pax</th>
                  <th class="px-4 py-3 text-left font-semibold">Encuentro</th>
                  <th class="px-4 py-3 text-left font-semibold">Habitación</th>
                  <th class="px-4 py-3 text-left font-semibold">Teléfono</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                <tr v-for="(item, index) in listado" :key="index" class="hover:bg-blue-50">
                  <td class="px-4 py-3">
                    <span class="px-2 py-0.5 rounded-full text-xs font-bold bg-blue-100 text-blue-800">
                      {{ item.hora }}
                    </span>
                  </td>
                  <td class="px-4 py-3 font-semibold text-gray-900">{{ item.nombre }}</td>
                  <td class="px-4 py-3 text-gray-700">{{ item.pasajeros }}</td>
                  <td class="px-4 py-3 text-gray-700">{{ item.encuentro }}</td>
                  <td class="px-4 py-3 text-gray-700">{{ item.habitacion || '—' }}</td>
                  <td class="px-4 py-3">
                    <div class="flex flex-col gap-1">
                      <!-- Botón WhatsApp -->
                      <a 
                        v-if="item.telefono" 
                        :href="getWhatsAppLink(item.telefono, item.nombre)"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="inline-flex items-center gap-1 text-green-700 hover:text-green-900 text-xs font-medium"
                      >
                        <span>💬</span>
                        <span>WhatsApp</span>
                      </a>
                      <!-- Enlace de llamada (más discreto) -->
                      <a 
                        v-if="item.telefono"
                        :href="'tel:' + item.telefono"
                        class="text-xs text-gray-500 hover:text-gray-700"
                      >
                        {{ item.telefono }}
                      </a>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          
        </div>

        <!-- Footer con nota de validez -->
        <div class="text-center pt-4 border-t border-gray-200">
          <p class="text-xs text-gray-500 bg-gray-100 inline-block px-4 py-2 rounded-full">
            ⏱️ Esta planilla tiene una duración de 24 horas desde su creación.
          </p>
          <p class="text-xs text-gray-400 mt-3">
            Generado: {{ formatTimestamp(timestamp) }}
          </p>
        </div>

      </div>

    </main>

    <!-- Footer fijo -->
    <footer class="bg-gray-900 text-gray-400 text-center py-3 text-xs mt-auto">
      TourPass Operations © {{ new Date().getFullYear() }} · 
      <span class="text-green-400">● Sistema en línea</span>
    </footer>

  </div>
</template>

<script>
/* eslint-disable no-unused-vars */
import { apiService } from '@/services/api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export default {
  name: 'RecogidaView',
  
  props: {
    encodedParam: { type: String, required: true }
  },

  data() {
    return {
      loading: false,
      error: null,
      
      // Datos del servicio
      environment: null,
      destino: null,        // ← Nuevo campo para "Destino / Tour"
      fechaServicio: null,
      cantidadPax: null,
      conductor: null,
      guia: null,
      grupo: null,
      timestamp: null,
      
      // Listado de pasajeros
      listado: []
    };
  },

  created() {
    this.fetchRecogida();
  },

  methods: {
    async fetchRecogida() {
      this.loading = true;
      this.error = null;
      
      try {
        const response = await apiService.getRecogidaEncoded(this.encodedParam);
        const data = response;
        
        if (data.ok && data.data) {
          this.environment = data.environment;
          
          // ✅ Mapeo correcto desde data.data
          this.destino = data.data.excursion;
          this.fechaServicio = data.data.fecha_servicio;
          this.cantidadPax = data.data.cantidad_pax;
          this.conductor = data.data.conductor;
          this.guia = data.data.guia;
          this.grupo = data.data.grupo;
          this.timestamp = data.timestamp;
          this.listado = Array.isArray(data.data.listado) ? data.data.listado : [];
          
        } else {
          if (!data.ok){
            this.error = 'Enlace caducado';
          }else{
            this.error = data.error || 'No se pudieron cargar los datos';
          }
          
          console.log('Imprimiendo else ', this.error)
        }
        
      } catch (err) {
        console.error('❌ Error fetching recogida:', err);
        
        if (err.response?.status === 401) {
          this.error = 'Clave de API inválida. Contacta al administrador.';
        } else if (err.response?.status === 403) {
          this.error = 'Acceso denegado.';
        } else if (err.response?.status === 404) {
          this.error = 'Servicio no encontrado.';
        } else if (err.response?.status >= 500) {
          this.error = 'Error del servidor. Intenta más tarde.';
        } else {
          this.error = err.message || 'Error de conexión';
        }
      } finally {
        this.loading = false;
      }
    },

    // ✅ Formatear fecha: compatible con formato DD-MM-YYYY de Laravel
    formatDate(dateString) {
      if (!dateString) return '—';
      try {
        // Si viene en formato DD-MM-YYYY (desde Laravel)
        if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
          const [day, month, year] = dateString.split('-');
          return `${day}/${month}/${year}`;
        }
        // Si viene en formato ISO o timestamp
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        return date.toLocaleDateString('es-CL', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
      } catch {
        return dateString;
      }
    },

    // Formatear timestamp completo (para footer)
    formatTimestamp(isoString) {
      if (!isoString) return '—';
      try {
        return new Date(isoString).toLocaleString('es-CL');
      } catch {
        return isoString;
      }
    },

    // ✅ Exportar a PDF optimizado para móvil/guía
    async exportToPDF() {
      if (this.listado.length === 0) return;
      
      try {
        // Mostrar estado de carga
        const originalBtnText = event?.target?.innerText || 'PDF';
        if (event?.target) event.target.disabled = true;
        if (event?.target?.querySelector('span:last-child')) {
          event.target.querySelector('span:last-child').innerText = 'Generando...';
        }

        const doc = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a4'
        });

        // Header del PDF
        doc.setFillColor(30, 64, 175); // blue-800
        doc.rect(0, 0, 210, 35, 'F');
        
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text('Planilla de Pasajeros', 14, 15);
        
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.text('Registro oficial y control de pasajeros para servicio de transporte', 14, 22);
        
        // Info del servicio en grid
        doc.setTextColor(30, 64, 175);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        
        let y = 42;
        const colWidth = 48;
        
        // Fila 1
        doc.text('Destino:', 14, y);
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'normal');
        doc.text(this.destino || '—', 14, y + 4);
        
        doc.setTextColor(30, 64, 175);
        doc.setFont('helvetica', 'bold');
        doc.text('Fecha:', 14 + colWidth, y);
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'normal');
        doc.text(this.formatDate(this.fechaServicio), 14 + colWidth, y + 4);
        
        doc.setTextColor(30, 64, 175);
        doc.setFont('helvetica', 'bold');
        doc.text('Grupo:', 14 + colWidth * 2, y);
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'normal');
        doc.text('G -' + this.grupo || '—', 14 + colWidth * 2, y + 4);
        
        doc.setTextColor(30, 64, 175);
        doc.setFont('helvetica', 'bold');
        doc.text('Pax:', 14 + colWidth * 3, y);
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'normal');
        doc.text(String(this.cantidadPax || '—'), 14 + colWidth * 3, y + 4);
        
        // Fila 2: Conductor y Guía
        y += 14;
        doc.setFillColor(220, 252, 231); // green-100
        doc.rect(14, y - 3, 90, 12, 'F');
        doc.setTextColor(22, 101, 52); // green-700
        doc.setFont('helvetica', 'bold');
        doc.text(`Conductor: ${this.conductor || 'Sin asignar'}`, 18, y + 3);
        
        doc.setFillColor(243, 232, 255); // purple-100
        doc.rect(106, y - 3, 90, 12, 'F');
        doc.setTextColor(107, 33, 168); // purple-700
        doc.text(`Guía: ${this.guia || 'Sin asignar'}`, 110, y + 3);
        
        // Tabla de pasajeros
        const tableData = this.listado.map((item, idx) => [
          String(idx + 1),
          item.hora,
          item.nombre,
          item.pasajeros,
          item.encuentro,
          item.habitacion || '—',
          item.telefono || '—'
        ]);

        y += 20;
        autoTable(doc, {
          startY: y,
          head: [['#', 'Hora', 'Pasajero', 'Pax', 'Encuentro', 'Habitación', 'Teléfono']],
          body: tableData,
          theme: 'grid',
          headStyles: {
            fillColor: [30, 64, 175],
            textColor: 255,
            fontSize: 9,
            fontStyle: 'bold'
          },
          bodyStyles: {
            fontSize: 8,
            cellPadding: 3
          },
          columnStyles: {
            0: { cellWidth: 10, halign: 'center' }, // #
            1: { cellWidth: 18, halign: 'center' }, // Hora
            2: { cellWidth: 40 }, // Pasajero
            3: { cellWidth: 22 }, // Pax
            4: { cellWidth: 35 }, // Encuentro
            5: { cellWidth: 20 }, // Habitación
            6: { cellWidth: 30 }  // Teléfono
          },
          margin: { left: 14, right: 14 }
        });

        // Footer con nota de validez
        const finalY = doc.lastAutoTable.finalY + 10;
        doc.setFontSize(8);
        doc.setTextColor(100, 116, 139); // gray-500
        doc.setFont('helvetica', 'italic');
        
        doc.setFontSize(7);
        doc.text(`Generado: ${this.formatTimestamp(this.timestamp)}`, 105, finalY + 5, { align: 'center' });
        doc.text(`TourPass Operations · ${new Date().getFullYear()}`, 105, finalY + 9, { align: 'center' });

        // Guardar PDF
        const fileName = `Planilla_${this.grupo || 'Servicio'}_${this.formatDate(this.fechaServicio).replace(/\//g, '-')}.pdf`;
        doc.save(fileName);

      } catch (err) {
        console.error('❌ Error generando PDF:', err);
        alert('No se pudo generar el PDF. Intenta nuevamente.');
      } finally {
        // Restaurar botón
        if (event?.target) {
          event.target.disabled = false;
          if (event.target.querySelector('span:last-child')) {
            event.target.querySelector('span:last-child').innerText = 'PDF';
          }
        }
      }
    },
    // ✅ Generar enlace de WhatsApp con mensaje personalizado (versión corregida)
    getWhatsAppLink(phone, passengerName) {
      if (!phone) return null;
      
      // 1. Limpiar número: quitar espacios, guiones, paréntesis (sin escapar innecesariamente)
      let cleanPhone = phone.replace(/[\s\-()]/g, '');
            
      // 3. Quitar el '+' para la URL de WhatsApp (wa.me no lo acepta)
      cleanPhone = cleanPhone.replace('+', '');
      
      // 4. Crear mensaje personalizado
      const message = `Hola ${passengerName}, te escribo desde TourPass para confirmar tu servicio de transporte.`;
      
      // 5. Codificar mensaje para URL
      const encodedMessage = encodeURIComponent(message);
      
      // 6. Retornar enlace completo
      return `https://wa.me/${cleanPhone}?text=${encodedMessage}`;
    }
  }
};
</script>

<style scoped>
/* Optimizaciones para impresión/PDF */
@media print {
  body {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  header, footer {
    position: static !important;
  }
}

/* Animaciones suaves */
tr {
  transition: background-color 0.15s ease-in-out;
}

/* Mejorar legibilidad en móviles */
@media (max-width: 768px) {
  table {
    font-size: 0.875rem;
  }
  th, td {
    padding: 0.75rem 0.5rem !important;
  }
}

/* Enlaces telefónicos */
a[href^="tel:"] {
  text-decoration: none;
}
a[href^="tel:"]:hover {
  text-decoration: underline;
}
</style>